// src/contexts/FavoritesContext.tsx
import { createContext, useContext, useState, useEffect, useMemo, ReactNode } from 'react'
import { useAuth } from './AuthContext'
import { createFavoritesService } from '../services/favoritesService'

export interface FavoriteItem {
  movie_id: string
  watched: boolean
}

interface CachedFavorites {
  data: FavoriteItem[]
  timestamp: number
  userId: string
}

interface FavoritesContextType {
  favorites: FavoriteItem[]
  displayedFavorites: FavoriteItem[]
  loading: boolean
  hideWatched: boolean
  setHideWatched: (value: boolean) => void
  toggleFavorite: (movieId: string) => Promise<void>
  setWatchedStatus: (movieId: string, watched: boolean) => Promise<void>
  isFavorite: (movieId: string) => boolean
}

const FavoritesContext = createContext<FavoritesContextType | undefined>(undefined)

export const useFavoritesContext = (): FavoritesContextType => {
  const context = useContext(FavoritesContext)
  if (!context) {
    throw new Error('useFavoritesContext must be used within a FavoritesProvider')
  }
  return context
}

// Cache expiration time: 5 minutes
const CACHE_EXPIRY_MS = 5 * 60 * 1000

const getCacheKey = (userId: string) => `userFavorites_${userId}`

const clearAllFavoritesCaches = () => {
  Object.keys(localStorage).forEach(key => {
    if (key.startsWith('userFavorites_')) {
      localStorage.removeItem(key)
    }
  })
}

const loadFromCache = (userId: string): FavoriteItem[] | null => {
  try {
    const cacheKey = getCacheKey(userId)
    const cached = localStorage.getItem(cacheKey)
    if (!cached) return null

    const cachedData: CachedFavorites = JSON.parse(cached)

    if (cachedData.userId !== userId) {
      localStorage.removeItem(cacheKey)
      return null
    }

    const now = Date.now()
    if (now - cachedData.timestamp > CACHE_EXPIRY_MS) {
      localStorage.removeItem(cacheKey)
      return null
    }

    return cachedData.data
  } catch {
    return null
  }
}

const saveToCache = (userId: string, favoritesData: FavoriteItem[]) => {
  try {
    const cacheKey = getCacheKey(userId)
    const cacheData: CachedFavorites = {
      data: favoritesData,
      timestamp: Date.now(),
      userId,
    }
    localStorage.setItem(cacheKey, JSON.stringify(cacheData))
  } catch {
    // ignore storage errors
  }
}

interface FavoritesProviderProps {
  children: ReactNode
}

export const FavoritesProvider = ({ children }: FavoritesProviderProps) => {
  const { user, loading: authLoading } = useAuth()
  const [favorites, setFavorites] = useState<FavoriteItem[]>([])
  const [loading, setLoading] = useState(false)
  const [hideWatched, setHideWatchedState] = useState(false)

  const favoritesService = useMemo(() => createFavoritesService(), [])

  // Load hideWatched preference from localStorage once on mount
  useEffect(() => {
    const stored = localStorage.getItem('hideWatchedMovies')
    if (stored) {
      try {
        setHideWatchedState(JSON.parse(stored))
      } catch {
        // ignore
      }
    }
  }, [])

  const setHideWatched = (value: boolean) => {
    setHideWatchedState(value)
    localStorage.setItem('hideWatchedMovies', JSON.stringify(value))
  }

  const loadFavorites = async (userId: string) => {
    setLoading(true)
    try {
      const userFavorites = await favoritesService.getUserFavorites(userId)
      setFavorites(userFavorites)
      saveToCache(userId, userFavorites)
    } catch (error) {
      console.error('Failed to load favorites:', error)
      setFavorites([])
    } finally {
      setLoading(false)
    }
  }

  // Single effect: runs once when auth resolves
  useEffect(() => {
    if (authLoading) return

    if (user) {
      // Show cached data immediately while fetching fresh data
      const cached = loadFromCache(user.id)
      if (cached) {
        setFavorites(cached)
      }
      loadFavorites(user.id)
    } else {
      setFavorites([])
      clearAllFavoritesCaches()
    }
  }, [user, authLoading]) // eslint-disable-line react-hooks/exhaustive-deps

  const toggleFavorite = async (movieId: string) => {
    if (!user) return

    const isFav = favorites.some(fav => fav.movie_id === movieId)

    try {
      if (isFav) {
        await favoritesService.removeFavorite(user.id, movieId)
        const updated = favorites.filter(fav => fav.movie_id !== movieId)
        setFavorites(updated)
        saveToCache(user.id, updated)
      } else {
        await favoritesService.addFavorite(user.id, movieId)
        const updated = [...favorites, { movie_id: movieId, watched: false }]
        setFavorites(updated)
        saveToCache(user.id, updated)
      }
    } catch (error) {
      console.error('Failed to toggle favorite:', error)
      // Re-sync from server on error
      loadFavorites(user.id)
    }
  }

  const setWatchedStatus = async (movieId: string, watched: boolean) => {
    if (!user) return

    // Optimistic update
    const updated = favorites.map(fav =>
      fav.movie_id === movieId ? { ...fav, watched } : fav
    )
    setFavorites(updated)
    saveToCache(user.id, updated)

    try {
      await favoritesService.updateFavoriteWatchedStatus(user.id, movieId, watched)
    } catch (error) {
      console.error('Failed to update watched status:', error)
      // Revert on error
      const reverted = favorites.map(fav =>
        fav.movie_id === movieId ? { ...fav, watched: !watched } : fav
      )
      setFavorites(reverted)
      saveToCache(user.id, reverted)
    }
  }

  const displayedFavorites = useMemo(() => {
    if (!hideWatched) return favorites
    return favorites.filter(fav => !fav.watched)
  }, [favorites, hideWatched])

  const isFavorite = (movieId: string) =>
    favorites.some(fav => fav.movie_id === movieId)

  const value: FavoritesContextType = {
    favorites,
    displayedFavorites,
    loading,
    hideWatched,
    setHideWatched,
    toggleFavorite,
    setWatchedStatus,
    isFavorite,
  }

  return (
    <FavoritesContext.Provider value={value}>
      {children}
    </FavoritesContext.Provider>
  )
}
